import { useCallback, useEffect, useState } from 'react';
import { api, type Recall, type RecallLookup, type RecallState, type RecallStatus, type Vehicle } from '../lib/api.ts';
import { formatDay, usePrefs } from '../lib/prefs.tsx';

type RecallRow = Recall & { status: RecallStatus | null };

/**
 * Open safety recalls for the vehicle, from NHTSA, with completion tracking.
 *
 * Two honesty rules govern this panel. Results are matched on year, make and
 * model rather than on the VIN, so a campaign listed here may not apply to this
 * particular vehicle; only the manufacturer can confirm that. And when the
 * model name cannot be resolved, the panel says so instead of showing an empty
 * list, because "we could not ask" and "there is nothing" are very different
 * answers and only one of them is safe to act on.
 */
export function RecallsPanel({ vehicle }: { vehicle: Vehicle | null }) {
  const [data, setData] = useState<RecallLookup | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const vin = vehicle?.vin ?? null;

  const load = useCallback(async () => {
    if (!vin) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setData(await api.recalls(vin));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [vin]);

  useEffect(() => {
    // Clear first. Holding the previous vehicle's campaigns on screen under a
    // new vehicle's heading is worse than showing nothing for a moment.
    setData(null);
    setOpen(null);
    void load();
  }, [load]);

  if (!vehicle) return null;

  const outstanding = data?.openCount ?? 0;
  const sorted = data ? [...data.recalls].sort(byNewestFirst) : [];

  return (
    <div className="card">
      <div className="row wrap" style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0, flex: 1 }}>Safety recalls</h2>
        {loading && <span className="pill">checking…</span>}
        {data && !data.unresolved && (
          <>
            <span className={`pill ${outstanding ? 'warn' : 'ok'}`}>
              <i className="dot" />
              {outstanding ? `${outstanding} outstanding` : 'none outstanding'}
            </span>
            {data.recalls.length > outstanding && (
              <span className="pill">{data.recalls.length - outstanding} closed</span>
            )}
          </>
        )}
        {data?.unresolved && <span className="pill crit">could not verify</span>}
      </div>

      {error && <div className="banner error small">{error}</div>}
      {data?.notice && <div className="banner warn small">{data.notice}</div>}

      {data && !data.unresolved && (
        <p className="small dim" style={{ marginTop: 0 }}>
          Matched on {data.modelYear} {data.make} <span className="mono">{data.matchedModels.join(', ')}</span>. NHTSA
          publishes recalls by model rather than by VIN, so confirm with a dealer whether a campaign applies to this
          vehicle. Recall work is performed free of charge.
        </p>
      )}

      {data && !data.unresolved && data.recalls.length > 0 && (
        <p className="small dim" style={{ marginTop: -4 }}>
          Every campaign below is listed by NHTSA against model year {data.modelYear}. The digits at the front of a
          campaign number are the year it was <em>filed</em>, not the vehicle's year, so a {data.modelYear} vehicle can
          carry a campaign numbered 26V. A summary may also name other models covered by the same filing.
        </p>
      )}

      {data && !data.unresolved && data.recalls.length === 0 && (
        <div className="empty">
          <strong>No open campaigns</strong>
          NHTSA lists no recalls for this year, make and model.
        </div>
      )}

      <div className="stack" style={{ gap: 8 }}>
        {sorted.map((r) => (
          <RecallRowView
            key={r.campaignNumber}
            recall={r}
            vin={vehicle.vin}
            expanded={open === r.campaignNumber}
            onToggle={() => setOpen(open === r.campaignNumber ? null : r.campaignNumber)}
            onChanged={load}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Newest first.
 *
 * Completed campaigns stay in date order rather than being pushed down; their
 * struck-through number and dimmed row already carry that state, and a
 * chronological list is easier to reconcile against a service history.
 * Undated records sort last instead of leading.
 */
function byNewestFirst(a: RecallRow, b: RecallRow): number {
  return (b.reportReceivedDate ?? '0000-00-00').localeCompare(a.reportReceivedDate ?? '0000-00-00');
}

const isClosed = (s?: RecallState) => s === 'completed' || s === 'not_applicable';

function RecallRowView({
  recall,
  vin,
  expanded,
  onToggle,
  onChanged,
}: {
  recall: RecallRow;
  vin: string;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => void;
}) {
  const { dateFormat } = usePrefs();
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState<string | null>(null);
  const [completedOn, setCompletedOn] = useState(recall.status?.completedOn ?? today());
  const [reference, setReference] = useState(recall.status?.reference ?? '');
  const [note, setNote] = useState(recall.status?.note ?? '');

  const status = recall.status;
  const closed = isClosed(status?.status);

  async function apply(next: RecallState, force = false) {
    setSaving(true);
    setConflict(null);
    try {
      await api.setRecallStatus(vin, recall.campaignNumber, {
        status: next,
        completedOn: next === 'completed' ? completedOn : null,
        reference: reference || null,
        note: note || null,
        force,
      });
      onChanged();
    } catch (err) {
      // 409 means a dealer record already says otherwise.
      setConflict((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function reset() {
    setSaving(true);
    try {
      await api.clearRecallStatus(vin, recall.campaignNumber);
      onChanged();
    } finally {
      setSaving(false);
    }
  }

  return (
    <article className={`recall${closed ? ' closed' : ''}`}>
      <button className="recall-head" aria-expanded={expanded} onClick={onToggle}>
        <span className="mono campaign">{recall.campaignNumber}</span>
        <span className="component">
          {recall.component ?? 'Component not stated'}
          <span className="applies">
            {[recall.modelYear, recall.make, recall.model].filter(Boolean).join(' ') || recall.matchedModel}
          </span>
        </span>
        <span className="published">
          <span className="date">{formatDay(recall.reportReceivedDate, dateFormat)}</span>
          <span className="dim">published</span>
        </span>
        <StatusChip status={status} />
      </button>

      {expanded && (
        <div className="recall-body">
          {recall.summary && (
            <>
              <span className="eyebrow">What is wrong</span>
              <p className="small">{recall.summary}</p>
            </>
          )}
          {recall.consequence && (
            <>
              <span className="eyebrow">Risk</span>
              <p className="small">{recall.consequence}</p>
            </>
          )}
          {recall.remedy && (
            <>
              <span className="eyebrow">Remedy</span>
              <p className="small">{recall.remedy}</p>
            </>
          )}

          <hr className="divider" />
          <span className="eyebrow">Completion</span>

          {status && (
            <p className="small dim" style={{ marginTop: 0 }}>
              Marked <strong>{label(status.status)}</strong>
              {status.completedOn ? ` on ${formatDay(status.completedOn, dateFormat)}` : ''} from{' '}
              {status.source === 'dealer' ? 'dealer records' : 'a manual entry'}
              {status.reference ? `, reference ${status.reference}` : ''}.
              {status.note ? ` ${status.note}` : ''}
            </p>
          )}

          {conflict && <div className="banner warn small">{conflict}</div>}

          {!closed && (
            <div className="grid-2" style={{ margin: '0 0 10px' }}>
              <label className="field">
                <span>Date completed</span>
                <input type="text" value={completedOn} onChange={(e) => setCompletedOn(e.target.value)} placeholder="YYYY-MM-DD" />
              </label>
              <label className="field">
                <span>Repair order / reference</span>
                <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="optional" />
              </label>
            </div>
          )}

          {!closed && (
            <label className="field" style={{ marginBottom: 10 }}>
              <span>Note</span>
              <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Which dealer, what was done" />
            </label>
          )}

          <div className="row wrap">
            {!closed ? (
              <>
                <button className="primary" onClick={() => apply('completed')} disabled={saving}>
                  Mark completed
                </button>
                <button onClick={() => apply('scheduled')} disabled={saving}>
                  Scheduled
                </button>
                <button onClick={() => apply('not_applicable')} disabled={saving}>
                  Not applicable
                </button>
              </>
            ) : (
              <>
                <button onClick={() => apply('open', status?.source === 'dealer')} disabled={saving}>
                  Reopen
                </button>
                <button className="ghost" onClick={reset} disabled={saving}>
                  Clear marking
                </button>
              </>
            )}
          </div>

          <p className="small dim" style={{ marginTop: 10, marginBottom: 0 }}>
            Applies to {[recall.modelYear, recall.make, recall.model].filter(Boolean).join(' ')}. Queried as{' '}
            <span className="mono">{recall.matchedModel}</span>
            {recall.manufacturer ? ` by ${recall.manufacturer}` : ''}. Published{' '}
            {formatDay(recall.reportReceivedDate, dateFormat)}.
          </p>
        </div>
      )}
    </article>
  );
}

function StatusChip({ status }: { status: RecallStatus | null }) {
  if (!status || status.status === 'open') {
    return (
      <span className="pill warn">
        <i className="dot" />
        outstanding
      </span>
    );
  }
  const tone = status.status === 'completed' ? 'ok' : status.status === 'scheduled' ? 'info' : '';
  return (
    <span className={`pill ${tone}`} title={status.source === 'dealer' ? 'From dealer records' : 'Entered by hand'}>
      <i className="dot" />
      {label(status.status)}
      {status.source === 'dealer' ? ' ✓' : ''}
    </span>
  );
}

function label(s: RecallState): string {
  return { open: 'outstanding', scheduled: 'scheduled', completed: 'completed', not_applicable: 'not applicable' }[s];
}

const today = () => new Date().toISOString().slice(0, 10);

